import { VersionActionType, type VersionEntryDto } from '@/api';
import {
  useSimulationDetails,
  useSimulationVersions,
} from '@/pages/price-planning/simulation/simulation-queries';
import { themeColors } from '@/theme/colors';
import { normalizeApiError } from '@/utils/error-utils';
import { Flex, Spinner, Text } from '@lego/klik-ui';
import { formatDistanceToNow } from 'date-fns';
import { useMemo } from 'react';
import { useSimulationReadOnly } from '../context/simulation-read-only-context';
import { CurrentVersionStatus } from '../utils/simulation-types';
import { VersionCard } from './version-card';

type Props = {
  simulationId: number;
};

const getActionLabel = (version: VersionEntryDto) => {
  const hasAssigneeChange =
    version.previousAssignedTo?.id != null &&
    version.previousAssignedTo.id !== version.simulationAssignedTo.id;

  switch (version.actionType) {
    case VersionActionType.SimulationCreated:
      return 'created the simulation';
    case VersionActionType.StatusChanged:
      return hasAssigneeChange
        ? 'updated the simulation'
        : 'changed simulation status';
    case VersionActionType.OwnershipChanged:
      return 'changed simulation ownership';
    case VersionActionType.Published:
      return 'published the simulation';
    case VersionActionType.VersionRestored:
      return 'restored the simulation version';
    case VersionActionType.ManualSnapshot:
      return 'saved a manual snapshot';
    default:
      return 'updated the simulation';
  }
};

export const VersionHistoryDrawerContent = ({ simulationId }: Props) => {
  const {
    data: versions,
    isLoading,
    isError,
    error,
  } = useSimulationVersions(simulationId);
  const {
    data: simulationDetails,
    isLoading: isSimulationDetailsLoading,
    isError: isSimulationDetailsError,
  } = useSimulationDetails(simulationId);
  const { selectedVersionId, setSelectedVersionId } = useSimulationReadOnly();

  const assignedToContact = simulationDetails?.assignedTo;
  const currentVersionActorName = assignedToContact?.name;
  const currentVersionError =
    isSimulationDetailsError || !currentVersionActorName
      ? 'Current simulation details are unavailable.'
      : null;

  const sorted = useMemo(
    () =>
      [...(versions ?? [])].sort((a, b) => {
        const aTime = a.createdAt?.getTime() ?? 0;
        const bTime = b.createdAt?.getTime() ?? 0;
        return bTime - aTime;
      }),
    [versions],
  );

  if (isLoading || isSimulationDetailsLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="120px">
        <Spinner size="md" />
      </Flex>
    );
  }

  if (isError) {
    const normalized = normalizeApiError(error);
    // Backend returns 404 for unauthorized resources to avoid leaking existence.
    const isAccessDenied =
      normalized.status === 403 || normalized.status === 404;
    return (
      <Text className="body-sm-regular" color={themeColors.content.default}>
        {isAccessDenied
          ? 'You do not have access to version history for this simulation.'
          : 'Failed to load version history.'}
      </Text>
    );
  }

  if (!versions?.length) {
    return (
      <Flex flexDirection="column" gap="12px">
        {currentVersionError && (
          <Text className="body-sm-regular" color={themeColors.content.default}>
            {currentVersionError}
          </Text>
        )}
        <Text className="body-sm-regular" color={themeColors.content.default}>
          No versions available.
        </Text>
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" gap="12px">
      {currentVersionActorName ? (
        <VersionCard
          isSelected={selectedVersionId === null}
          onSelect={() => setSelectedVersionId(null)}
          status={CurrentVersionStatus}
          actorName={currentVersionActorName}
          actionLabel="is working on the simulation"
          timeLabel="Now"
        />
      ) : (
        <Text className="body-sm-regular" color={themeColors.content.default}>
          {currentVersionError ?? 'Current simulation details are unavailable.'}
        </Text>
      )}
      {sorted.map((version) => (
        <VersionCard
          key={version.id}
          isSelected={selectedVersionId === version.id}
          onSelect={() => setSelectedVersionId(version.id)}
          status={version.simulationStatus}
          previousStatus={version.previousStatus}
          actorName={version.createdBy.name}
          actionLabel={getActionLabel(version)}
          previousAssigneeName={version.previousAssignedTo?.name}
          assigneeName={version.simulationAssignedTo.name}
          message={version.message}
          timeLabel={
            version.createdAt
              ? formatDistanceToNow(version.createdAt, { addSuffix: true })
              : ''
          }
        />
      ))}
    </Flex>
  );
};
